import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { LabUploadProblem, UploadVerdict } from '../../../lab-manager/models/lab-upload-result';

/**
 * Lists what is wrong with a submission's results, and says whether it can go on.
 *
 * Opened from two places: straight after an upload, and before READY FOR AGRONOMIST.
 * Named for what it shows rather than for either trigger.
 *
 * <p>In BLOCKING mode the only button closes with false. The dialog cannot be talked
 * into returning true, so a future caller cannot accidentally proceed past a hard block
 * by passing the wrong flag.
 *
 * <p>Not built on DraftConfirmationDialogComponent: that renders two interpolated
 * strings and cannot show an itemised list. Widening it would put every one of its
 * callers across Lab Manager, Field Details and Soil Crop Nutrition at risk for the sake
 * of one new screen.
 */
export interface ResultsProblemsDialogData {
  verdict: UploadVerdict;
  dialogTitle: string;
  summary: string;
  problems: LabUploadProblem[];
  /** Problems the API found but did not send, because it caps its list too. */
  problemsOmitted?: number;
  /** The label on the proceed button. Ignored when the verdict is blocking. */
  confirmText?: string;
}

@Component({
  selector: 'app-results-problems-dialog',
  templateUrl: './results-problems-dialog.component.html',
  styleUrls: ['./results-problems-dialog.component.css'],
  standalone: false
})
export class ResultsProblemsDialogComponent implements OnInit {

  /**
   * How many problems are listed before the rest are summarised in one line.
   *
   * A file with a wrong layout can produce a problem per row. Two thousand of them
   * would build two thousand DOM nodes inside a modal, and nobody reads past the first
   * few anyway: the point is to show enough to identify the fault.
   */
  static readonly MAX_SHOWN = 50;

  dialogTitle = '';
  summary = '';
  verdict: UploadVerdict = 'ok';
  confirmText = 'Continue anyway';

  shown: LabUploadProblem[] = [];
  hiddenCount = 0;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ResultsProblemsDialogData,
    public dialogRef: MatDialogRef<ResultsProblemsDialogComponent>
  ) {
  }

  ngOnInit(): void {
    this.dialogTitle = this.data?.dialogTitle ?? 'Results';
    this.summary = this.data?.summary ?? '';
    this.verdict = this.data?.verdict ?? 'ok';
    this.confirmText = this.data?.confirmText ?? 'Continue anyway';

    const problems = this.data?.problems ?? [];
    this.shown = problems.slice(0, ResultsProblemsDialogComponent.MAX_SHOWN);

    // Two caps compound: the API sends at most a couple of hundred, and this shows at
    // most fifty of those. Both have to be counted or the line under-reports.
    this.hiddenCount = Math.max(0, problems.length - this.shown.length)
      + (this.data?.problemsOmitted ?? 0);
  }

  get isBlocking(): boolean {
    return this.verdict === 'blocking';
  }

  isBlockingProblem(problem: LabUploadProblem): boolean {
    return problem?.severity === 'BLOCKING';
  }

  /** "Row 4, sample 100-1-A1", leaving out whichever part is not known. */
  locationOf(problem: LabUploadProblem): string {
    const parts: string[] = [];
    if (problem?.rowNumber != null) {
      parts.push(`Row ${problem.rowNumber}`);
    }
    if (problem?.sampleNumber) {
      parts.push(`sample ${problem.sampleNumber}`);
    }
    return parts.join(', ');
  }

  closeDialog(proceed: boolean): void {
    // A blocking dialog can only ever close false, whatever it is asked for.
    this.dialogRef.close(proceed && !this.isBlocking);
  }
}
